import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Spinner from "../components/Spinner";
import { checkEligibility } from "../utils/ageEligibility.js";
import jsPDF from "jspdf";

function AdminDashboard() {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [teamSearch, setTeamSearch] = useState("");
  const [playerSearch, setPlayerSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);

  const CATEGORY_OPTIONS = ["ALL", "U13 Boys", "U15 Boys", "U17 Boys", "Girls"];

  const token = localStorage.getItem("adminToken");

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }

    Promise.all([
      fetch(`${process.env.REACT_APP_API_URL}/api/admin/teams`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then((res) => res.json()),
      fetch(`${process.env.REACT_APP_API_URL}/api/admin/players`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then((res) => res.json()),
    ])
      .then(([teamsData, playersData]) => {
        setTeams(Array.isArray(teamsData) ? teamsData : []);
        setPlayers(Array.isArray(playersData) ? playersData : []);
      })
      .finally(() => setLoading(false));
  }, [token, navigate]);

  const updateStatus = async (playerId, status) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/admin/players/${playerId}/verify`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      },
    );
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.error || "Failed to update player status. Please try again.");
      return;
    }
    const updated = await res.json();
    setPlayers((prev) =>
      prev.map((p) => (p._id === updated._id ? updated : p)),
    );
  };

  // Map teamId -> category so players (which only store a teamId) can be
  // filtered by category too.
  const teamCategoryById = teams.reduce((acc, t) => {
    acc[t.teamId] = t.category;
    return acc;
  }, {});

  const matchesCategory = (category) =>
    categoryFilter === "ALL" ||
    (category || "").trim().toLowerCase() === categoryFilter.toLowerCase();

  const teamQuery = teamSearch.toLowerCase();
  const filteredTeams = teams.filter(
    (t) =>
      ((t.teamName || "").toLowerCase().includes(teamQuery) ||
        (t.teamId || "").toLowerCase().includes(teamQuery)) &&
      matchesCategory(t.category),
  );

  const playerQuery = playerSearch.toLowerCase();
  const filteredPlayers = players.filter(
    (p) =>
      p.verificationStatus !== "VERIFIED" &&
      p.verificationStatus !== "REJECTED" &&
      ((p.fullName || "").toLowerCase().includes(playerQuery) ||
        (p.teamId || "").toLowerCase().includes(playerQuery)) &&
      matchesCategory(p.category || teamCategoryById[p.teamId]),
  );

  const selectedTeamPlayers = selectedTeam
    ? players.filter(
        (p) =>
          p.teamId === selectedTeam.teamId &&
          p.verificationStatus !== "REJECTED",
      )
    : [];
  const downloadTeamPdf = (team, teamPlayers) => {
    const doc = new jsPDF();
    let y = 20;

    doc.setFontSize(16);
    doc.text(team.teamName, 14, y);
    y += 8;

    doc.setFontSize(10);
    doc.text(`Team ID: ${team.teamId}`, 14, y);
    y += 6;
    doc.text(`County: ${team.county}`, 14, y);
    y += 6;
    doc.text(`Category: ${team.category}`, 14, y);
    y += 6;
    doc.text(`Coach / Manager: ${team.coachName}`, 14, y);
    y += 6;
    doc.text(`Phone: ${team.phone}`, 14, y);
    y += 6;
    doc.text(`Email: ${team.email}`, 14, y);
    y += 12;

    doc.setFontSize(12);
    doc.text(`Players (${teamPlayers.length})`, 14, y);
    y += 8;

    doc.setFontSize(10);
    doc.text("#", 14, y);
    doc.text("Name", 24, y);
    doc.text("Date of Birth", 110, y);
    doc.text("Age", 170, y);
    y += 2;
    doc.line(14, y, 196, y);
    y += 6;

    teamPlayers.forEach((p, index) => {
      if (y > 280) {
        doc.addPage();
        y = 20;
      }
      const { age } = checkEligibility(p.dob, team.category);
      doc.text(`${index + 1}`, 14, y);
      doc.text(p.fullName, 24, y);
      doc.text(new Date(p.dob).toLocaleDateString(), 110, y);
      doc.text(age !== null && age !== undefined ? `${age}` : "-", 170, y);

      y += 7;
    });

    doc.save(`${team.teamId}_roster.pdf`);
  };

  const downloadCategoryPdf = () => {
    const list = teams
      .filter((t) => matchesCategory(t.category) && t.teamName)
      .sort(
        (a, b) =>
          (a.category || "").localeCompare(b.category || "") ||
          a.teamName.localeCompare(b.teamName),
      );

    if (list.length === 0) {
      alert("No teams to print for this category.");
      return;
    }

    const doc = new jsPDF();
    const fit = (text, width) =>
      doc.splitTextToSize(String(text || "-"), width)[0];
    let y = 20;

    doc.setFontSize(16);
    doc.text(
      `Teams - ${categoryFilter === "ALL" ? "All Categories" : categoryFilter}`,
      14,
      y,
    );
    y += 8;

    doc.setFontSize(10);
    doc.text(
      `Printed: ${new Date().toLocaleDateString()}  |  Total teams: ${list.length}`,
      14,
      y,
    );
    y += 10;

    const drawHeader = () => {
      doc.setFont(undefined, "bold");
      doc.text("#", 14, y);
      doc.text("Team Name", 22, y);
      doc.text("Coach Name", 85, y);
      doc.text("Coach Number", 132, y);
      doc.text("Category", 166, y);
      doc.setFont(undefined, "normal");
      y += 2;
      doc.line(14, y, 196, y);
      y += 6;
    };

    drawHeader();

    list.forEach((t, index) => {
      if (y > 280) {
        doc.addPage();
        y = 20;
        drawHeader();
      }
      doc.text(`${index + 1}`, 14, y);
      doc.text(fit(t.teamName, 60), 22, y);
      doc.text(fit(t.coachName, 44), 85, y);
      doc.text(fit(t.phone, 32), 132, y);
      doc.text(fit(t.category, 30), 166, y);
      y += 7;
    });

    const label =
      categoryFilter === "ALL" ? "all" : categoryFilter.replace(/\s+/g, "_");
    doc.save(`teams_${label}.pdf`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-cream p-6">
        <h1 className="text-2xl font-bold text-brand-red mb-4">
          Admin Dashboard
        </h1>
        <Spinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="text-2xl font-bold text-brand-red">Admin Dashboard</h1>

        <div className="flex items-center gap-2">
          <label htmlFor="categoryFilter" className="text-sm text-gray-500">
            Category
          </label>
          <select
            id="categoryFilter"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="border rounded-lg p-2 text-sm bg-white"
          >
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {c === "ALL" ? "All Categories" : c}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-xs text-gray-500">
            {categoryFilter === "ALL"
              ? "Total Teams"
              : `Teams (${categoryFilter})`}
          </p>
          <p className="text-lg font-bold text-brand-dark">
            {teams.filter((t) => matchesCategory(t.category)).length}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-xs text-gray-500">
            {categoryFilter === "ALL"
              ? "Total Players"
              : `Players (${categoryFilter})`}
          </p>
          <p className="text-lg font-bold text-brand-dark">
            {
              players.filter((p) =>
                matchesCategory(p.category || teamCategoryById[p.teamId]),
              ).length
            }
          </p>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-xs text-gray-500">Verified</p>
          <p className="text-lg font-bold text-green-600">
            {
              players.filter(
                (p) =>
                  p.verificationStatus === "VERIFIED" &&
                  matchesCategory(p.category || teamCategoryById[p.teamId]),
              ).length
            }
          </p>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-xs text-gray-500">Pending</p>
          <p className="text-lg font-bold text-yellow-600">
            {
              players.filter(
                (p) =>
                  p.verificationStatus === "PENDING" &&
                  matchesCategory(p.category || teamCategoryById[p.teamId]),
              ).length
            }
          </p>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow p-5 mb-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h2 className="text-lg font-bold text-brand-dark">Teams</h2>
          <button
            onClick={downloadCategoryPdf}
            className="bg-brand-red text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-red-800"
          >
            Print PDF
          </button>
        </div>
        <input
          placeholder="Search by team name or ID..."
          value={teamSearch}
          onChange={(e) => setTeamSearch(e.target.value)}
          className="w-full border rounded-lg p-2 text-sm mb-3"
        />

        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2">Team ID</th>
                <th className="py-2">Team Name</th>
                <th className="py-2">County</th>
                <th className="py-2">Coach</th>
                <th className="py-2">Category</th>
                <th className="py-2">Payment</th>
              </tr>
            </thead>
            <tbody>
              {filteredTeams.map((t) => (
                <tr
                  key={t._id}
                  onClick={() => setSelectedTeam(t)}
                  className="border-b cursor-pointer hover:bg-gray-50"
                >
                  <td className="py-2">{t.teamId}</td>
                  <td className="py-2">{t.teamName}</td>
                  <td className="py-2">{t.county}</td>
                  <td className="py-2">{t.coachName}</td>
                  <td className="py-2">{t.category}</td>
                  <td className="py-2">{t.paymentStatus}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="md:hidden space-y-3">
          {filteredTeams.map((t) => (
            <div
              key={t._id}
              onClick={() => setSelectedTeam(t)}
              className="border rounded-lg p-3 text-sm cursor-pointer hover:bg-gray-50"
            >
              <p className="font-bold text-brand-dark">{t.teamName}</p>
              <p className="text-gray-500">{t.teamId}</p>
              <p>County: {t.county}</p>
              <p>Coach: {t.coachName}</p>
              <p>Category: {t.category}</p>
              <p>Payment: {t.paymentStatus}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow p-5">
        <h2 className="text-lg font-bold text-brand-dark mb-3">
          Pending Player Verification
        </h2>

        <input
          placeholder="Search by player name or team ID..."
          value={playerSearch}
          onChange={(e) => setPlayerSearch(e.target.value)}
          className="w-full border rounded-lg p-2 text-sm mb-3"
        />

        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2">Name</th>
                <th className="py-2">Team</th>
                <th className="py-2">DOB</th>
                <th className="py-2">Status</th>
                <th className="py-2">Reg #</th>
                <th className="py-2">Documents</th>
                <th className="py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPlayers.map((p) => (
                <tr key={p._id} className="border-b">
                  <td className="py-2">{p.fullName}</td>
                  <td className="py-2">{p.teamId}</td>
                  <td className="py-2">
                    {new Date(p.dob).toLocaleDateString()}
                  </td>
                  <td className="py-2">{p.verificationStatus}</td>
                  <td className="py-2">{p.regNumber || "-"}</td>
                  <td className="py-2">
                    <div className="flex flex-wrap gap-1.5">
                      {p.photoUrl && (
                        <a
                          href={p.photoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-colors"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            className="w-3.5 h-3.5"
                          >
                            <path
                              fillRule="evenodd"
                              d="M1 5.25A2.25 2.25 0 013.25 3h13.5A2.25 2.25 0 0119 5.25v9.5A2.25 2.25 0 0116.75 17H3.25A2.25 2.25 0 011 14.75v-9.5zm1.5 5.81v3.69c0 .414.336.75.75.75h13.5a.75.75 0 00.75-.75v-2.69l-2.22-2.219a.75.75 0 00-1.06 0l-1.91 1.909.47.47a.75.75 0 11-1.06 1.06L6.53 8.091a.75.75 0 00-1.06 0l-2.97 2.97zM12 7a1 1 0 11-2 0 1 1 0 012 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Photo
                        </a>
                      )}
                      {p.birthCertUrl && (
                        <a
                          href={p.birthCertUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 hover:border-gray-300 transition-colors"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            className="w-3.5 h-3.5"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4 2a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6.414A2 2 0 0017.414 5L15 2.586A2 2 0 0013.586 2H4zm3 8a1 1 0 000 2h6a1 1 0 100-2H7zm0 4a1 1 0 100 2h4a1 1 0 100-2H7z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Birth Cert
                        </a>
                      )}
                      {!p.photoUrl && !p.birthCertUrl && (
                        <span className="text-gray-400 text-xs">None</span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 space-x-2">
                    <button
                      onClick={() => updateStatus(p._id, "VERIFIED")}
                      className="text-green-600 font-semibold"
                    >
                      Verify
                    </button>
                    <button
                      onClick={() => updateStatus(p._id, "REJECTED")}
                      className="text-red-600 font-semibold"
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="md:hidden space-y-3">
          {filteredPlayers.map((p) => (
            <div key={p._id} className="border rounded-lg p-3 text-sm">
              <p className="font-bold text-brand-dark">{p.fullName}</p>
              <p className="text-gray-500">Team: {p.teamId}</p>
              <p>DOB: {new Date(p.dob).toLocaleDateString()}</p>
              <p>Status: {p.verificationStatus}</p>
              <p>Reg #: {p.regNumber || "-"}</p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {p.photoUrl && (
                  <a
                    href={p.photoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="w-3.5 h-3.5"
                    >
                      <path
                        fillRule="evenodd"
                        d="M1 5.25A2.25 2.25 0 013.25 3h13.5A2.25 2.25 0 0119 5.25v9.5A2.25 2.25 0 0116.75 17H3.25A2.25 2.25 0 011 14.75v-9.5zm1.5 5.81v3.69c0 .414.336.75.75.75h13.5a.75.75 0 00.75-.75v-2.69l-2.22-2.219a.75.75 0 00-1.06 0l-1.91 1.909.47.47a.75.75 0 11-1.06 1.06L6.53 8.091a.75.75 0 00-1.06 0l-2.97 2.97zM12 7a1 1 0 11-2 0 1 1 0 012 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Photo
                  </a>
                )}
                {p.birthCertUrl && (
                  <a
                    href={p.birthCertUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 hover:border-gray-300 transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="w-3.5 h-3.5"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4 2a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6.414A2 2 0 0017.414 5L15 2.586A2 2 0 0013.586 2H4zm3 8a1 1 0 000 2h6a1 1 0 100-2H7zm0 4a1 1 0 100 2h4a1 1 0 100-2H7z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Birth Cert
                  </a>
                )}
                {!p.photoUrl && !p.birthCertUrl && (
                  <span className="text-gray-400 text-xs">None</span>
                )}
              </div>
              <div className="flex gap-3 mt-2">
                <button
                  onClick={() => updateStatus(p._id, "VERIFIED")}
                  className="text-green-600 font-semibold"
                >
                  Verify
                </button>
                <button
                  onClick={() => updateStatus(p._id, "REJECTED")}
                  className="text-red-600 font-semibold"
                >
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedTeam && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedTeam(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-lg w-full max-w-2xl max-h-[85vh] overflow-y-auto p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-brand-dark">
                  {selectedTeam.teamName}
                </h2>
                <p className="text-sm text-gray-500">{selectedTeam.teamId}</p>
              </div>
              <button
                onClick={() => setSelectedTeam(null)}
                className="text-gray-400 hover:text-gray-700 text-xl leading-none"
              >
                &times;
              </button>
            </div>

            <button
              onClick={() => downloadTeamPdf(selectedTeam, selectedTeamPlayers)}
              className="mb-4 bg-brand-red text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-red-800"
            >
              Download PDF
            </button>

            <div className="grid grid-cols-2 gap-3 text-sm mb-5">
              <div>
                <p className="text-xs text-gray-500">County</p>
                <p className="font-semibold text-brand-dark">
                  {selectedTeam.county}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Category</p>
                <p className="font-semibold text-brand-dark">
                  {selectedTeam.category}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Coach / Manager</p>
                <p className="font-semibold text-brand-dark">
                  {selectedTeam.coachName}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Payment Status</p>
                <p className="font-semibold text-brand-dark">
                  {selectedTeam.paymentStatus}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Phone</p>
                <p className="font-semibold text-brand-dark">
                  {selectedTeam.phone}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="font-semibold text-brand-dark">
                  {selectedTeam.email}
                </p>
              </div>
            </div>

            <h3 className="text-sm font-bold text-brand-dark mb-2">
              Players ({selectedTeamPlayers.length})
            </h3>

            {selectedTeamPlayers.length === 0 ? (
              <p className="text-sm text-gray-400">
                No players registered yet.
              </p>
            ) : (
              <div className="space-y-2">
                {selectedTeamPlayers.map((p) => (
                  <div
                    key={p._id}
                    className="border rounded-lg p-3 text-sm flex items-center justify-between gap-3"
                  >
                    <div>
                      <p className="font-semibold text-brand-dark">
                        {p.fullName}
                      </p>
                      <p className="text-gray-500">
                        DOB: {new Date(p.dob).toLocaleDateString()} · Reg #:{" "}
                        {p.regNumber || "-"}
                      </p>
                      <p className="text-gray-500">
                        Status: {p.verificationStatus}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-1.5 shrink-0">
                      {p.photoUrl && (
                        <a
                          href={p.photoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-colors"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            className="w-3.5 h-3.5"
                          >
                            <path
                              fillRule="evenodd"
                              d="M1 5.25A2.25 2.25 0 013.25 3h13.5A2.25 2.25 0 0119 5.25v9.5A2.25 2.25 0 0116.75 17H3.25A2.25 2.25 0 011 14.75v-9.5zm1.5 5.81v3.69c0 .414.336.75.75.75h13.5a.75.75 0 00.75-.75v-2.69l-2.22-2.219a.75.75 0 00-1.06 0l-1.91 1.909.47.47a.75.75 0 11-1.06 1.06L6.53 8.091a.75.75 0 00-1.06 0l-2.97 2.97zM12 7a1 1 0 11-2 0 1 1 0 012 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Photo
                        </a>
                      )}
                      {p.birthCertUrl && (
                        <a
                          href={p.birthCertUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 hover:border-gray-300 transition-colors"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            className="w-3.5 h-3.5"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4 2a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6.414A2 2 0 0017.414 5L15 2.586A2 2 0 0013.586 2H4zm3 8a1 1 0 000 2h6a1 1 0 100-2H7zm0 4a1 1 0 100 2h4a1 1 0 100-2H7z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Birth Cert
                        </a>
                      )}
                      {!p.photoUrl && !p.birthCertUrl && (
                        <span className="text-gray-400 text-xs">None</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
